import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { StatusBadge, fmtDate, fmtCurrency } from "../lib/status";
import { Search, AlertTriangle, ShieldCheck, ArrowRight, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n/I18nProvider";
import NoIndex from "../components/NoIndex";

const STEP_SEARCH = "search";
const STEP_VERIFY = "verify";
const STEP_VIEW = "view";

export default function FindBooking() {
  const { t } = useT();
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ company_id: "", booking_reference: "", email: "", phone: "" });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(STEP_SEARCH);
  const [otpSession, setOtpSession] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [unlocked, setUnlocked] = useState(null);

  useEffect(() => {
    api.get("/public/companies").then((r) => setCompanies(r.data));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/public/find-booking", form);
      setResults(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const requestOtp = async (lookup_id) => {
    try {
      const { data } = await api.post("/public/find-booking/request-otp", { lookup_id });
      setOtpSession(data);
      setOtpCode("");
      setOtpError("");
      setStep(STEP_VERIFY);
      toast.success(`Code sent to ${data.destination_masked}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setOtpError("");
    try {
      const { data } = await api.post("/public/find-booking/verify-otp", {
        session_id: otpSession.session_id, code: otpCode.trim(),
      });
      setUnlocked(data);
      setStep(STEP_VIEW);
      toast.success("Verified. Here's your booking.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const reason = detail?.reason;
      let msg = detail?.detail || "Verification failed";
      if (reason === "wrong_code" && typeof detail?.attempts_left === "number") {
        msg += `: ${detail.attempts_left} attempt${detail.attempts_left === 1 ? "" : "s"} left`;
      }
      setOtpError(msg);
    } finally {
      setVerifying(false);
    }
  };

  const startOver = () => {
    setStep(STEP_SEARCH);
    setResults(null);
    setOtpSession(null);
    setUnlocked(null);
    setOtpCode("");
    setOtpError("");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-8" data-testid="find-booking-page">
      <NoIndex title="Find My Booking · ConfirmPH" />
      <Stepper step={step} />

      {step === STEP_SEARCH && (
        <>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold">{t("find.title")}</h1>
            <p className="mt-2 text-slate-600">{t("find.subtitle")}</p>
          </div>

          <form onSubmit={submitSearch} className="rounded-xl border border-slate-200 bg-white p-6 grid sm:grid-cols-2 gap-4">
            <label className="sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Company</span>
              <select data-testid="fb-company" value={form.company_id} onChange={set("company_id")} className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
                <option value="">Any company</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}{c.city ? ` · ${c.city}` : ""}</option>)}
              </select>
            </label>
            <F label="Booking reference (e.g. BK-XXXXXXXX)" testId="fb-ref" value={form.booking_reference} onChange={set("booking_reference")} required />
            <F label="Email used to book" type="email" testId="fb-email" value={form.email} onChange={set("email")} />
            <F label="Phone used to book" testId="fb-phone" value={form.phone} onChange={set("phone")} />
            <p className="sm:col-span-2 text-xs text-slate-500">
              Enter the booking reference plus either the email or phone used for the booking.
            </p>
            <div className="sm:col-span-2">
              <button type="submit" disabled={loading} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60" data-testid="fb-submit">
                <Search className="w-4 h-4" /> {loading ? "Searching…" : "Search"}
              </button>
            </div>
          </form>

          {results && (
            <div className="space-y-3" data-testid="fb-results">
              {results.count === 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
                    <div className="text-sm text-amber-900">
                      <p className="font-medium">{t("find.noresult.title")}</p>
                      <p className="mt-1">{t("find.noresult.body")}</p>
                      <Link to="/request-confirmation" data-testid="fb-go-request" className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg btn-brand font-medium text-white">
                        {t("find.cta.request")}
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              {results.results?.length > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 flex items-start gap-2" data-testid="fb-possible-match">
                  <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0" />
                  <p>
                    We found {results.count} possible booking{results.count === 1 ? "" : "s"}. To protect customer privacy, please verify using the code we'll send to the contact details connected to the booking.
                  </p>
                </div>
              )}
              {results.results?.map((b) => (
                <div key={b.lookup_id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`fb-match-${b.lookup_id}`}>
                  <div className="flex flex-wrap justify-between gap-2">
                    <div>
                      <div className="text-sm text-slate-500">{b.company?.name}{b.company?.city ? ` · ${b.company.city}` : ""}</div>
                      <div className="font-display text-lg font-semibold">{b.vehicle?.name || "-"}</div>
                      <div className="text-xs text-slate-500 font-mono">{b.booking_reference}</div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Name on booking: {b.customer_name_masked}</div>
                  <button
                    onClick={() => requestOtp(b.lookup_id)}
                    data-testid={`fb-verify-btn-${b.lookup_id}`}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg btn-brand text-sm font-medium"
                  >
                    Verify with code <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {step === STEP_VERIFY && otpSession && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5" data-testid="fb-verify-card">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium">
              <Mail className="w-3.5 h-3.5" /> Code sent
            </div>
            <h2 className="mt-3 font-display text-2xl font-semibold">Confirm it's your booking</h2>
            <p className="mt-1 text-sm text-slate-600">
              We sent a 6-digit code to <span className="font-mono">{otpSession.destination_masked}</span>. It expires in 10 minutes.
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Since real email is mocked for this preview, you can find the code in the company's <span className="font-medium">Notifications log</span> (or on the page that just delivered it). In production, it would arrive in your inbox.
            </p>
          </div>
          <form onSubmit={submitOtp} className="space-y-3">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">6-digit code</span>
              <input
                data-testid="fb-otp-input"
                type="text" inputMode="numeric" maxLength={6} required
                value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                className="mt-1.5 w-full px-3.5 py-3 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none text-center font-mono text-2xl tracking-[0.5em]"
              />
            </label>
            {otpError && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="fb-otp-error">{otpError}</div>
            )}
            <div className="flex flex-wrap gap-2">
              <button type="submit" disabled={verifying || otpCode.length !== 6} className="px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60" data-testid="fb-otp-submit">
                {verifying ? "Verifying…" : "Verify"}
              </button>
              <button type="button" onClick={startOver} className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm" data-testid="fb-otp-back">
                Back to search
              </button>
            </div>
          </form>
        </div>
      )}

      {step === STEP_VIEW && unlocked && (
        <div className="space-y-5" data-testid="fb-view">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            <p className="text-sm text-emerald-900">Verified. Here are your full booking details.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{unlocked.company?.name}</div>
                <div className="font-mono text-sm text-slate-700 mt-0.5">{unlocked.booking.booking_reference}</div>
                <h2 className="font-display text-2xl font-semibold mt-2">{unlocked.vehicle?.name || "-"}</h2>
              </div>
              <StatusBadge status={unlocked.booking.status} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-sm border-t border-slate-100 pt-4">
              <Item label="Pickup" value={fmtDate(unlocked.booking.start_datetime)} sub={unlocked.booking.pickup_location} />
              <Item label="Return" value={fmtDate(unlocked.booking.end_datetime)} sub={unlocked.booking.return_location} />
              <Item label="Price" value={fmtCurrency(unlocked.booking.price)} mono />
              <Item label="Deposit required" value={fmtCurrency(unlocked.booking.deposit_required)} mono />
              <Item label="Name on booking" value={unlocked.booking.customer_name} />
              <Item label="Email" value={unlocked.booking.customer_email} />
            </div>
            {unlocked.booking.customer_visible_notes && (
              <div className="border-t border-slate-100 pt-4">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Important notes</div>
                <p className="text-sm mt-1 whitespace-pre-line">{unlocked.booking.customer_visible_notes}</p>
              </div>
            )}
            {unlocked.confirmation_link && (
              <a
                href={unlocked.confirmation_link}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg btn-brand font-semibold"
                data-testid="fb-go-confirm"
              >
                Confirm or request a change <ArrowRight className="w-4 h-4" />
              </a>
            )}
          </div>
          <button onClick={startOver} className="text-sm text-slate-600 hover:underline">Search another booking</button>
        </div>
      )}
    </div>
  );
}

function Stepper({ step }) {
  const steps = [
    { key: STEP_SEARCH, label: "Search" },
    { key: STEP_VERIFY, label: "Match" },
    { key: STEP_VIEW, label: "View" },
  ];
  const activeIdx = steps.findIndex((s) => s.key === step);
  return (
    <ol className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em]">
      {steps.map((s, i) => (
        <li key={s.key} className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full grid place-items-center font-display text-sm ${
            i <= activeIdx ? "bg-[#1B4332] text-white" : "bg-slate-200 text-slate-500"
          }`}>{i + 1}</span>
          <span className={i <= activeIdx ? "text-slate-900" : "text-slate-400"}>{s.label}</span>
          {i < steps.length - 1 && <span className="w-6 h-px bg-slate-300" />}
        </li>
      ))}
    </ol>
  );
}

function F({ label, value, onChange, type = "text", testId, required = false }) {
  return (
    <label>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <input data-testid={testId} type={type} value={value} onChange={onChange} required={required}
        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
    </label>
  );
}

function Item({ label, value, sub, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className={`mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
      {sub && <div className="text-sm text-slate-600">{sub}</div>}
    </div>
  );
}
